import type { ConnectionSupervisorState } from "./connectionSupervisor";

/**
 * Canonical connection presentation, ported from upstream T3 Code's
 * `client-runtime/connection/presentation.ts` (pin e3e7cc3f). Every surface
 * that renders a connection phase renders its copy from here so the wording
 * cannot drift per coordinator.
 */

export type ConnectionPresentationPhase =
  | "available"
  | "offline"
  | "connecting"
  | "reconnecting"
  | "connected"
  | "error"
  | "unsupported";

export interface ConnectionPresentation {
  readonly phase: ConnectionPresentationPhase;
  readonly error: string | null;
  readonly traceId: string | null;
}

export function presentConnectionState(state: ConnectionSupervisorState): ConnectionPresentation {
  switch (state.phase) {
    case "available":
      return { phase: "available", error: null, traceId: null };
    case "offline":
      return { phase: "offline", error: null, traceId: null };
    case "connecting":
      return {
        phase: state.attempt <= 1 && state.lastFailure === null ? "connecting" : "reconnecting",
        error: state.lastFailure?.message ?? null,
        traceId: null,
      };
    case "connected":
      return { phase: "connected", error: null, traceId: null };
    case "backoff":
      return {
        phase: "reconnecting",
        error: state.lastFailure?.message ?? null,
        traceId: null,
      };
    case "blocked":
      return {
        phase: state.lastFailure?.reason === "unsupported" ? "unsupported" : "error",
        error: state.lastFailure?.message ?? null,
        traceId: null,
      };
  }
}

export function connectionStatusText(connection: ConnectionPresentation): string {
  switch (connection.phase) {
    case "available":
      return "Available";
    case "offline":
      return "Offline";
    case "connecting":
      return "Connecting...";
    case "reconnecting":
      return connection.error
        ? `Failed to connect. Reconnecting... Reason: ${connection.error}`
        : "Reconnecting...";
    case "connected":
      return "Connected";
    case "unsupported":
      return "Client not supported";
    case "error":
      return connection.error
        ? `Connection failed. Reason: ${connection.error}`
        : "Connection failed";
  }
}

export function connectionStatusTitle(connection: ConnectionPresentation): string {
  if (connection.phase === "reconnecting" && connection.error) {
    return "Failed to connect. Reconnecting...";
  }
  return connectionStatusText({ ...connection, error: null });
}

// ── Cloud session recovery copy ──────────────────────────────────────────────
// The expiry/revoke UX (desktop-cloud recovery toast, web redirect copy) is
// rendered from these constants, so the wording lives beside the rest of the
// canonical connection copy instead of inside one coordinator component.

export const CLOUD_SESSION_EXPIRED_TITLE = "Cloud session expired";
export const CLOUD_SESSION_EXPIRED_DESCRIPTION = "Your cloud session has expired or been revoked.";
export const CLOUD_SESSION_RECONNECTING_DESCRIPTION = "Reconnecting to Forge Cloud...";
export const CLOUD_SESSION_RECONNECT_ACTION = "Reconnect to Cloud";
export const CLOUD_SESSION_SWITCH_TO_LOCAL_ACTION = "Switch to Local";
